import React, { useEffect } from 'react';
import { ChartCard, HorizontalBars, InfoTooltip } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';
import {
  type RelativePerformance,
  getRelativePerformance,
} from '../-helpers/getRelativePerformance';
import { useSortedChartData } from '../-hooks/useSortedChartData';
import { OptionalKey } from '../route';

export const KPIImpact_EmployeesTrainedFragment = gql(/* GraphQL */ `
  fragment KPIImpact_EmployeesTrainedFragment on HealthcareIndicators {
    pctEmployeesTrained {
      trimmedAvg
      isDataSuppressed
    }
  }
`);

type EmployeesTrainedProps = {
  className?: string;
  indicators: FragmentType<typeof KPIImpact_EmployeesTrainedFragment>;
  viewerIndicators?: FragmentType<typeof KPIImpact_EmployeesTrainedFragment>;
  investmentLevel: OptionalKey;
  onEvaluatePerformance: (key: string, performance: RelativePerformance) => void;
};

export function EmployeesTrained({
  indicators,
  viewerIndicators,
  investmentLevel,
  className,
  onEvaluatePerformance,
}: EmployeesTrainedProps) {
  const benchmarkData = useFragment(KPIImpact_EmployeesTrainedFragment, indicators);
  const viewerData = useFragment(KPIImpact_EmployeesTrainedFragment, viewerIndicators);

  const benchmarkDataMap = {
    company: benchmarkData.pctEmployeesTrained,
    investment: benchmarkData.pctEmployeesTrained,
    rate_of_change: benchmarkData.pctEmployeesTrained,
  };

  const viewerDataMap = {
    company: viewerData?.pctEmployeesTrained,
    investment: viewerData?.pctEmployeesTrained,
    rate_of_change: viewerData?.pctEmployeesTrained,
  };

  const selectedBenchmarkData = benchmarkDataMap[investmentLevel];
  const selectedViewerData = viewerDataMap[investmentLevel];

  const { t, title, titleTooltipKPI } = useChartTranslations('employeesTrained');

  const getDisplayValue = (value?: number | null): string => {
    if (value == null) return '';
    const strValue = (value * 100).toLocaleString('en', { maximumFractionDigits: 0 });
    return t('displayValue', { value: strValue }) ?? strValue;
  };

  const { sortedChartData } = useSortedChartData({
    selectedBenchmarkData,
    selectedViewerData,
    getDisplayValue,
  });

  const relativePerformance = getRelativePerformance(
    selectedBenchmarkData?.trimmedAvg,
    selectedViewerData?.trimmedAvg
  );

  useEffect(() => {
    onEvaluatePerformance('employeesTrained', relativePerformance);
  }, [onEvaluatePerformance, relativePerformance]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header className="flex items-center justify-between">
        <ChartCard.Title>
          {title}
          {titleTooltipKPI && <InfoTooltip>{titleTooltipKPI}</InfoTooltip>}
        </ChartCard.Title>
        {/* <Button variant="outline" className="shrink-0 font-semibold uppercase">
          view details
        </Button> */}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={selectedBenchmarkData.isDataSuppressed}>
        <HorizontalBars min={0} max={1} showInlineDisplayValue={true}>
          {sortedChartData.map((data, index) => (
            <HorizontalBars.Item
              key={index}
              label={data.label}
              value={data.value}
              displayValue={data.displayValue}
              color={data.color}
              isDashed={data.isDashed}
            />
          ))}
        </HorizontalBars>

        {/* <Link href="/impact-kpis/land-managed" className="mt-5 lg:hidden">
          <Button variant="outline" size="small" className="shrink-0 font-semibold uppercase">
            view details
          </Button>
        </Link> */}
      </ChartCard.Body>
    </ChartCard>
  );
}
